/**
 * prohats-reviews.js
 * ─────────────────────────────────────────────────────────────────────────
 * Web Component that handles the client-side state and interactive reviews
 * form. Matches components/product/product-reviews.tsx verbatim.
 * ─────────────────────────────────────────────────────────────────────────
 */

const INITIAL_REVIEWS = [
  {
    id: "1",
    name: "John D.",
    rating: 5,
    title: "Incredible quality!",
    comment: "This hat fits perfectly and the craftsmanship is second to none. Worth every penny.",
    date: "2026-05-15",
  },
  {
    id: "2",
    name: "Sarah M.",
    rating: 4,
    title: "Beautiful style",
    comment: "Absolutely love the look and feel. It runs slightly large, but with some minor adjustment it's perfect.",
    date: "2026-05-28",
  },
  {
    id: "3",
    name: "Cody R.",
    rating: 5,
    title: "Ranch tough",
    comment: "I wear this daily on the ranch. It holds up under rain, sun, and dirt. Highly recommend.",
    date: "2026-06-02",
  },
];

class ProHatsProductReviews extends HTMLElement {
  constructor() {
    super();
    this.productId = this.getAttribute('data-product-id') || 'default';
    this.showForm = false;
    this.rating = 5;

    // Load from local storage or fallback to INITIAL_REVIEWS
    const saved = localStorage.getItem(`ph_reviews_${this.productId}`);
    if (saved) {
      try {
        this.reviews = JSON.parse(saved);
      } catch {
        this.reviews = INITIAL_REVIEWS;
      }
    } else {
      this.reviews = INITIAL_REVIEWS;
    }
  }

  connectedCallback() {
    this.initElements();
    this.bindEvents();
    this.render();
  }

  initElements() {
    this.btnToggle = this.querySelector('.ph-btn-write-review');
    this.form = this.querySelector('.ph-review-form');
    this.listContainer = this.querySelector('.ph-reviews-list');
    this.starButtons = this.querySelectorAll('.ph-star-btn');
    this.averageStarsContainer = this.querySelector('.ph-reviews-average-stars');
    this.summaryText = this.querySelector('.ph-reviews-summary');

    // Form inputs
    this.inputName = this.querySelector('#review-author-name');
    this.inputTitle = this.querySelector('#review-title');
    this.inputComment = this.querySelector('#review-comment');
  }

  bindEvents() {
    if (this.btnToggle) {
      this.btnToggle.addEventListener('click', () => this.toggleForm());
    }

    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    this.starButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const val = parseInt(btn.getAttribute('data-value') || '5', 10);
        this.setFormRating(val);
      });
    });
  }

  toggleForm() {
    this.showForm = !this.showForm;
    if (this.showForm) {
      this.form.classList.remove('ph-hidden');
      this.btnToggle.textContent = 'Cancel';
    } else {
      this.form.classList.add('ph-hidden');
      this.btnToggle.textContent = 'Write a Review';
    }
  }

  setFormRating(val) {
    this.rating = val;
    this.starButtons.forEach(btn => {
      const btnVal = parseInt(btn.getAttribute('data-value') || '0', 10);
      if (btnVal <= val) {
        btn.innerHTML = this.getStarSvg(true, 'h-7 w-7 text-amber-500');
      } else {
        btn.innerHTML = this.getStarSvg(false, 'h-7 w-7 text-stone-400 dark:text-stone-600');
      }
    });
  }

  handleSubmit(e) {
    e.preventDefault();
    const name = this.inputName.value.trim();
    const title = this.inputTitle.value.trim();
    const comment = this.inputComment.value.trim();

    if (!name || !title || !comment) return;

    const newReview = {
      id: Date.now().toString(),
      name,
      rating: this.rating,
      title,
      comment,
      date: new Date().toISOString().split('T')[0] || '',
    };

    this.reviews = [newReview, ...this.reviews];
    localStorage.setItem(`ph_reviews_${this.productId}`, JSON.stringify(this.reviews));

    // Reset Form
    this.inputName.value = '';
    this.inputTitle.value = '';
    this.inputComment.value = '';
    this.setFormRating(5);
    this.toggleForm();

    // Re-render
    this.render();
  }

  getStarSvg(filled, classList = 'h-5 w-5 text-amber-500') {
    if (filled) {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="${classList}">
        <path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z" clip-rule="evenodd" />
      </svg>`;
    } else {
      return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="${classList}">
        <path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499c.15-.362.61-.362.76 0l2.25 5.413 5.827.468c.394.032.55.51.265.787L16.32 14.28l1.32 5.545c.088.373-.314.664-.649.467l-4.992-3.046-4.992 3.046c-.336.197-.738-.094-.65-.467L7.68 14.28 3.425 10.02c-.286-.277-.13-.755.265-.787l5.827-.468 2.25-5.413Z" />
      </svg>`;
    }
  }

  render() {
    // 1. Calculate average rating
    const average = this.reviews.length > 0 
      ? (this.reviews.reduce((acc, r) => acc + r.rating, 0) / this.reviews.length).toFixed(1)
      : '0.0';
    
    // 2. Render summary texts
    if (this.summaryText) {
      this.summaryText.textContent = `${average} out of 5 (${this.reviews.length} reviews)`;
    }

    // 3. Render average stars
    if (this.averageStarsContainer) {
      let starsHtml = '';
      const roundedAvg = Math.round(Number(average));
      for (let i = 1; i <= 5; i++) {
        starsHtml += this.getStarSvg(i <= roundedAvg);
      }
      this.averageStarsContainer.innerHTML = starsHtml;
    }

    // 4. Render reviews list
    if (this.listContainer) {
      if (this.reviews.length === 0) {
        this.listContainer.innerHTML = `<p class="ph-no-reviews-text text-sm text-stone-500">No reviews yet. Be the first to write a review!</p>`;
        return;
      }

      this.listContainer.innerHTML = this.reviews.map(review => {
        let stars = '';
        for (let i = 1; i <= 5; i++) {
          stars += this.getStarSvg(i <= review.rating, 'h-4 w-4 text-amber-500');
        }

        return `
          <div class="ph-review-card">
            <div class="ph-review-card-header">
              <div class="ph-review-card-stars-title">
                <div class="ph-review-stars flex">${stars}</div>
                <h4 class="ph-review-title">${this.escapeHtml(review.title)}</h4>
              </div>
              <span class="ph-review-date">${this.escapeHtml(review.date)}</span>
            </div>
            <p class="ph-review-author">by ${this.escapeHtml(review.name)}</p>
            <p class="ph-review-comment">${this.escapeHtml(review.comment)}</p>
          </div>
        `;
      }).join('');
    }
  }

  escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
}

customElements.define('prohats-product-reviews', ProHatsProductReviews);
